/**
 * Ballot measure helpers for the Policy page.
 * Data is sourced from Democracy Works elections (includeBallotData=true).
 */

export const BALLOT_SCOPE_ALL = "all";
export const BALLOT_SCOPE_STATE = "state";
export const BALLOT_SCOPE_LOCAL = "local";

/** Scope the ballot list opens on, and the one omitted from the URL. */
export const BALLOT_SCOPE_DEFAULT = BALLOT_SCOPE_STATE;

/** Scope filter chips shared by the ballot list and filter modal (web). */
export const BALLOT_SCOPE_CHIP_OPTIONS = [
  { id: BALLOT_SCOPE_STATE, label: "Statewide" },
  { id: BALLOT_SCOPE_LOCAL, label: "Local" },
];

export const LOCAL_DISTRICT_TYPES = new Set([
  "cityTown",
  "cityTownSubdivision",
  "county",
  "countySubdivision",
  "judicialDistrict",
  "judicialDistrictSubdivision",
  "schoolDistrict",
  "schoolDistrictSubdivision",
  "specialDistrict",
  "specialDistrictSubdivision",
]);

export const STATE_DISTRICT_TYPES = new Set([
  "state",
  "stateLegislativeLower",
  "stateLegislativeUpper",
  "stateSubdivision",
]);

export function normalizeBallotScope(scope) {
  if (
    scope === BALLOT_SCOPE_ALL ||
    scope === BALLOT_SCOPE_STATE ||
    scope === BALLOT_SCOPE_LOCAL
  ) return scope;
  return BALLOT_SCOPE_DEFAULT;
}

export function isLocalMeasure(districtType) {
  return LOCAL_DISTRICT_TYPES.has(districtType);
}

export function isStateMeasure(districtType) {
  if (!districtType) return true;
  if (STATE_DISTRICT_TYPES.has(districtType)) return true;
  if (LOCAL_DISTRICT_TYPES.has(districtType)) return false;
  return districtType !== "congress";
}

export function getMeasuresFromElection(election) {
  if (!election || typeof election !== "object") return [];
  const raw =
    election.ballotMeasures ||
    election.ballotData?.ballotMeasures ||
    election.ballotData?.referenda ||
    [];
  return Array.isArray(raw) ? raw : [];
}

/**
 * Flatten ballot measures from DW election payloads, dedupe by measure id.
 * @param {Array} elections
 * @param {string} stateCode
 */
export function extractMeasuresFromElections(elections, stateCode) {
  if (!Array.isArray(elections)) return [];
  const byId = new Map();
  const upperState = (stateCode || "").toUpperCase();

  elections.forEach((election) => {
    const electionDate =
      election.date ||
      election.electionDay ||
      election.election_date ||
      election.startDate ||
      null;
    const electionName =
      election.description ||
      election.name ||
      election.electionType ||
      "Election";

    getMeasuresFromElection(election).forEach((measure) => {
      if (!measure || typeof measure !== "object") return;
      const id = measure.id || measure.ocdId;
      if (!id) return;
      const key = String(id);
      if (byId.has(key)) return;

      byId.set(key, {
        ...measure,
        id: key,
        stateCode: upperState,
        electionDate,
        electionName,
        isBallotMeasure: true,
      });
    });
  });

  return Array.from(byId.values()).sort((a, b) => {
    const da = a.electionDate ? new Date(a.electionDate).getTime() : 0;
    const db = b.electionDate ? new Date(b.electionDate).getTime() : 0;
    if (da !== db) return da - db;
    return (a.shortName || a.name || "").localeCompare(b.shortName || b.name || "");
  });
}

export function filterMeasuresByScope(measures, scope) {
  const normalized = normalizeBallotScope(scope);
  if (normalized === BALLOT_SCOPE_ALL) return measures;
  if (normalized === BALLOT_SCOPE_STATE) {
    return measures.filter((m) => isStateMeasure(m.districtType));
  }
  return measures.filter((m) => isLocalMeasure(m.districtType));
}

export function filterMeasuresBySearch(measures, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return measures;
  return measures.filter((m) => {
    const haystack = [
      m.shortName,
      m.streamlinedName,
      m.name,
      m.summary,
      m.ballotQuestion,
      m.districtName,
      m.type,
      ...(Array.isArray(m.topics) ? m.topics : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

/**
 * Match measures against selected exact jurisdiction names.
 */
export function filterMeasuresByJurisdictions(measures, jurisdictions) {
  if (!jurisdictions || jurisdictions.length === 0) return measures;
  const set = new Set(jurisdictions);
  return (measures || []).filter((m) => {
    const name = (m.districtName || "").trim();
    return set.has(name);
  });
}

/**
 * Compose scope, optional local place filters, and search for the ballot list.
 * @param {Array} measures
 * @param {{ scope?: string, places?: string[], query?: string }} filters
 */
export function filterBallotMeasures(measures, { scope, places, query } = {}) {
  const normalizedScope = normalizeBallotScope(scope);
  let list = filterMeasuresByScope(measures, normalizedScope);
  if (normalizedScope === BALLOT_SCOPE_LOCAL) {
    list = filterMeasuresByJurisdictions(list, places);
  }
  return filterMeasuresBySearch(list, query);
}

export const BALLOT_EMPTY_REASON = {
  NO_STATE_SELECTED: "no_state",
  DATASET_EMPTY: "dataset_empty",
  SEARCH_NO_MATCH: "search_no_match",
  PLACES_NO_MATCH: "places_no_match",
  FILTER_NO_MATCH: "filter_no_match",
};

/**
 * Context-aware empty copy for the ballot list.
 * Returns null when the filtered list is non-empty.
 */
export function getBallotMeasuresEmptyState({
  selectedState,
  scope,
  stateName = "",
  query = "",
  places = [],
  allMeasures = [],
  filteredMeasures = [],
}) {
  if (!selectedState) {
    return {
      reason: BALLOT_EMPTY_REASON.NO_STATE_SELECTED,
      title: "Select a state to browse ballot measures",
      message: "Choose your state to see measures for upcoming elections.",
      showClearSearch: false,
      showClearPlaces: false,
      showBrowseLocal: false,
      browseLocalCount: 0,
      showBrowseStatewide: false,
      browseStatewideCount: 0,
    };
  }

  if (filteredMeasures.length > 0) {
    return null;
  }

  const normalizedScope = normalizeBallotScope(scope);
  const isLocalScope = normalizedScope === BALLOT_SCOPE_LOCAL;
  const trimmedQuery = String(query || "").trim();
  const hasSearch = trimmedQuery.length > 0;
  const placeList = Array.isArray(places) ? places.filter(Boolean) : [];
  const hasPlaces = isLocalScope && placeList.length > 0;

  const scopeMeasures = filterMeasuresByScope(allMeasures, normalizedScope);
  const localCount = allMeasures.filter((m) => isLocalMeasure(m.districtType)).length;
  const statewideCount = allMeasures.filter((m) => isStateMeasure(m.districtType)).length;
  const displayState = stateName || selectedState;
  const isDatasetEmpty = scopeMeasures.length === 0;

  if (isDatasetEmpty) {
    if (isLocalScope) {
      return {
        reason: BALLOT_EMPTY_REASON.DATASET_EMPTY,
        title: "No local measures published yet",
        message: `No local measures are published yet for upcoming ${displayState} elections. Local coverage is often added closer to election day and may not include every city or county.`,
        showClearSearch: false,
        showClearPlaces: false,
        showBrowseLocal: false,
        browseLocalCount: 0,
        showBrowseStatewide: statewideCount > 0,
        browseStatewideCount: statewideCount,
      };
    }
    return {
      reason: BALLOT_EMPTY_REASON.DATASET_EMPTY,
      title: "No statewide measures published yet",
      message: `No statewide measures are published yet for upcoming ${displayState} elections. That does not mean your ballot will have none—check back closer to election day.`,
      showClearSearch: false,
      showClearPlaces: false,
      showBrowseLocal: localCount > 0,
      browseLocalCount: localCount,
      showBrowseStatewide: false,
      browseStatewideCount: 0,
    };
  }

  if (hasSearch && hasPlaces) {
    const placeLabel =
      placeList.length === 1 ? placeList[0] : `${placeList.length} selected places`;
    return {
      reason: BALLOT_EMPTY_REASON.SEARCH_NO_MATCH,
      title: `No results for "${trimmedQuery}"`,
      message: `No local measures in ${placeLabel} match that search. Try different keywords or clear your filters.`,
      showClearSearch: true,
      showClearPlaces: true,
      showBrowseLocal: false,
      browseLocalCount: 0,
      showBrowseStatewide: false,
      browseStatewideCount: 0,
    };
  }

  if (hasSearch) {
    const scopeLabel = isLocalScope ? "local" : "statewide";
    return {
      reason: BALLOT_EMPTY_REASON.SEARCH_NO_MATCH,
      title: `No results for "${trimmedQuery}"`,
      message: `No ${scopeLabel} measures match your search. Try different keywords or clear search.`,
      showClearSearch: true,
      showClearPlaces: false,
      showBrowseLocal: false,
      browseLocalCount: 0,
      showBrowseStatewide: false,
      browseStatewideCount: 0,
    };
  }

  if (hasPlaces) {
    const placeLabel =
      placeList.length === 1 ? placeList[0] : `${placeList.length} selected places`;
    return {
      reason: BALLOT_EMPTY_REASON.PLACES_NO_MATCH,
      title: "No measures in selected places",
      message: `Nothing is published yet for ${placeLabel}. Local coverage may be incomplete or added closer to election day.`,
      showClearSearch: false,
      showClearPlaces: true,
      showBrowseLocal: false,
      browseLocalCount: 0,
      showBrowseStatewide: false,
      browseStatewideCount: 0,
    };
  }

  return {
    reason: BALLOT_EMPTY_REASON.FILTER_NO_MATCH,
    title: isLocalScope ? "No matching local measures" : "No matching statewide measures",
    message: "Try adjusting your filters or search.",
    showClearSearch: false,
    showClearPlaces: false,
    showBrowseLocal: false,
    browseLocalCount: 0,
    showBrowseStatewide: false,
    browseStatewideCount: 0,
  };
}

const BALLOT_STATUS_LABELS = {
  onBallot: "On ballot",
  notOnBallot: "Not on ballot",
  passed: "Passed",
  approved: "Approved",
  failed: "Failed",
  rejected: "Rejected",
  defeated: "Defeated",
  pending: "Pending",
};

export function formatBallotStatus(measure) {
  const raw = measure?.status || measure?.outcome;
  if (!raw) return null;
  if (BALLOT_STATUS_LABELS[raw]) return BALLOT_STATUS_LABELS[raw];
  return String(raw)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Distinct local districts present in the data, most measures first.
 * Used to offer quick-pick suggestions so users know which towns are covered.
 * @returns {Array<{name: string, count: number}>}
 */
export function getLocalJurisdictions(measures) {
  const counts = new Map();
  (measures || []).forEach((m) => {
    if (!isLocalMeasure(m.districtType)) return;
    const name = (m.districtName || "").trim();
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export function paginateMeasures(measures, page, pageSize) {
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const safeSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 15;
  const start = (safePage - 1) * safeSize;
  return {
    items: measures.slice(start, start + safeSize),
    totalCount: measures.length,
    page: safePage,
    pageCount: Math.max(1, Math.ceil(measures.length / safeSize)),
  };
}

export function formatMeasureDisplayName(measure) {
  return (
    measure?.shortName ||
    measure?.streamlinedName ||
    measure?.name ||
    "Ballot measure"
  );
}

export function getMeasureJurisdictionLabel(measure) {
  const district = measure?.districtName;
  const type = measure?.districtType;
  if (district && type) {
    const local = isLocalMeasure(type);
    return `${local ? "Local" : "State"} · ${district}`;
  }
  if (district) return district;
  if (type && isLocalMeasure(type)) return "Local";
  if (type && isStateMeasure(type)) return "Statewide";
  return measure?.stateCode || "";
}

export function formatElectionDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function stripHtml(html) {
  return (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export function sanitizeDwHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/ on[a-z]+="[^"]*"/gi, "")
    .replace(/ on[a-z]+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}

export function getMeasureSourceUrl(measure) {
  const candidates = [
    measure?.ballotpediaUrl,
    measure?.url,
    measure?.sourceUrl,
  ];
  for (const raw of candidates) {
    if (typeof raw === "string" && /^https?:\/\//i.test(raw.trim())) {
      return raw.trim();
    }
  }
  return null;
}

export function buildBallotMeasurePath(measure) {
  const state = (measure?.stateCode || "").toLowerCase();
  const id = encodeURIComponent(measure?.id || "");
  return `/bills/ballot/${state}/${id}`;
}

export function buildBallotListSearchParams({
  stateCode,
  scope,
  query,
  places,
  page,
}) {
  const p = new URLSearchParams();
  p.set("view", "ballot");
  if (stateCode && stateCode !== "FED") p.set("state", stateCode);
  if (scope && scope !== BALLOT_SCOPE_DEFAULT) p.set("scope", scope);
  if (query) p.set("q", query);
  if (Array.isArray(places) && places.length > 0) {
    places.forEach((place) => p.append("place", place));
  }
  if (page > 1) p.set("p", String(page));
  return p;
}

export function parseBallotListUrl(searchWithoutQuestionMark) {
  const params = new URLSearchParams(searchWithoutQuestionMark || "");
  const stateCode = params.get("state")?.toUpperCase() || "";
  const scope = normalizeBallotScope(params.get("scope") || BALLOT_SCOPE_DEFAULT);
  const q = params.get("q") || "";
  const places = params.getAll("place").filter(Boolean);
  const pRaw = parseInt(params.get("p"), 10);
  const page = Number.isFinite(pRaw) && pRaw > 0 ? pRaw : 1;
  return { stateCode, scope, q, places, page };
}
